import { apiRequest } from '../../../lib/api';
import { API_PATHS } from '../../../lib/apiRoutes';
import { buildIdempotencyKey, buildStickyIdempotencyKey } from '../../../lib/idempotency';

/**
 * Public contract for credits endpoints. Documents the route URLs and the
 * minimum payload shape each method requires so tests can assert that the
 * frontend stays aligned with the backend route module + Zod schemas.
 *
 * `adjust` mirrors the owner-only ledger correction endpoint
 * (`POST /credits/adjust`) which requires `{ userId, amount, reason, reasonCode }`.
 */
export const CREDITS_API_CONTRACT = {
  adjust: API_PATHS.credits.adjust,
  topup: API_PATHS.credits.topup,
  topupPreview: API_PATHS.credits.topupPreview,
  cashout: API_PATHS.credits.cashout,
  balance: API_PATHS.credits.balance,
  ledger: API_PATHS.credits.ledger,
  requiredPayloadKeys: {
    adjust: ['amount', 'reason', 'reasonCode'],
    topup: ['amountCents'],
    cashout: ['amountCents']
  }
} as const;

export interface CreditBalance {
  balance: number;
}

export interface TopupResult {
  topUpCents: number;
  bonusCents: number;
  campaignId?: string;
  ineligibleReason?:
    | 'NO_LIVE_CAMPAIGN'
    | 'AMOUNT_BELOW_LOWEST_TIER'
    | 'INSUFFICIENT_ORDER_HISTORY'
    | 'PER_USER_CAP_REACHED'
    | 'PER_USER_DAILY_LIMIT_REACHED'
    | 'GLOBAL_BUDGET_EXHAUSTED';
  paymentIntentId: string;
  newBalance: number;
}

export interface BonusPreview {
  amountCents: number;
  bonusCents: number;
  campaignId?: string;
  campaignName?: string;
  campaignEndsAt?: string;
  tierMinCents?: number;
  ineligibleReason?: TopupResult['ineligibleReason'];
}

export interface CashOutResult {
  approvalId: string;
  eligibleCashCents: number;
  bonusCents: number;
}

/** Matches server `ownerCreditAdjustmentReasonCodes`. */
export type OwnerCreditAdjustmentReasonCode =
  | 'OWNER_LEDGER_CORRECTION'
  | 'OWNER_SETTLEMENT_REMEDIATION'
  | 'OWNER_FRAUD_RECOVERY'
  | 'OWNER_CUSTOMER_GOODWILL'
  | 'OWNER_ORDER_RECOVERY';

export interface CreditLedgerEntryClient {
  id: string;
  userId: string;
  delta: number;
  reason: string;
  origin: string;
  creditClass?: 'PERMANENT' | 'SOFT';
  expiresAt?: string;
  referenceType?: string;
  referenceId?: string;
  createdAt: string;
}

export interface CreditsLedgerPage {
  items: CreditLedgerEntryClient[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreditAdjustResult {
  userId: string;
  amount: number;
  balance: number;
}

const unwrap = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    const data = (payload as { data: T }).data;
    if (data == null) {
      throw new Error('Server returned an empty response. Try again.');
    }
    return data;
  }
  if (payload == null) {
    throw new Error('Server returned an empty response. Try again.');
  }
  return payload as T;
};

const toIso = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

const normalizeLedgerEntry = (row: Record<string, unknown>): CreditLedgerEntryClient => ({
  id: String(row.id || row._id || ''),
  userId: String(row.userId || ''),
  delta: Number(row.delta ?? 0),
  reason: String(row.reason || ''),
  origin: String(row.origin || ''),
  creditClass: row.creditClass as CreditLedgerEntryClient['creditClass'],
  expiresAt: row.expiresAt ? toIso(row.expiresAt) : undefined,
  referenceType: row.referenceType != null ? String(row.referenceType) : undefined,
  referenceId: row.referenceId != null ? String(row.referenceId) : undefined,
  createdAt: toIso(row.createdAt) || new Date().toISOString()
});

export const creditsApi = {
  getBalance: async (): Promise<CreditBalance> => {
    const payload = await apiRequest<unknown>(API_PATHS.credits.balance);
    return unwrap<CreditBalance>(payload);
  },

  /**
   * Paginated credit ledger for the authenticated user (GET /credits/ledger).
   */
  ledger: async (opts?: { page?: number; limit?: number }): Promise<CreditsLedgerPage> => {
    const page = opts?.page ?? 1;
    const limit = opts?.limit ?? 100;
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    });
    const payload = await apiRequest<unknown>(`${API_PATHS.credits.ledger}?${qs.toString()}`);
    const raw = unwrap<{ items?: unknown[]; pagination?: CreditsLedgerPage['pagination'] }>(payload);
    const items = Array.isArray(raw.items)
      ? raw.items.map((row) => normalizeLedgerEntry(row as Record<string, unknown>))
      : [];
    const pagination = raw.pagination ?? {
      page,
      limit,
      total: items.length,
      totalPages: 1
    };
    return { items, pagination };
  },

  /**
   * Owner ledger adjustment (POST /credits/adjust). Requires OWNER role.
   */
  adjust: async (input: {
    userId: string;
    amount: number;
    reason: string;
    reasonCode?: OwnerCreditAdjustmentReasonCode;
    approvalId?: string;
    /** Optional stable key for a single owner intent (avoids random double-award on retry). */
    idempotencyKey?: string;
  }): Promise<CreditAdjustResult> => {
    const reasonCode = input.reasonCode ?? 'OWNER_LEDGER_CORRECTION';
    const payload = await apiRequest<unknown>(API_PATHS.credits.adjust, {
      method: 'POST',
      body: JSON.stringify({
        userId: input.userId,
        amount: input.amount,
        reason: input.reason,
        reasonCode,
        ...(input.approvalId ? { approvalId: input.approvalId } : {})
      }),
      idempotencyKey:
        input.idempotencyKey?.trim() ||
        buildStickyIdempotencyKey('credits-adjust', [input.userId, input.amount, reasonCode]),
      requireIdempotencyKey: true
    });
    const result = unwrap<CreditAdjustResult>(payload);
    if (!Number.isFinite(Number(result?.balance))) {
      throw new Error('Credit adjustment was not confirmed by the server.');
    }
    return result;
  },
  previewTopUp: async (amountCents: number): Promise<BonusPreview> => {
    const payload = await apiRequest<unknown>(
      `${API_PATHS.credits.topupPreview}?amountCents=${encodeURIComponent(String(amountCents))}`
    );
    return unwrap<BonusPreview>(payload);
  },
  previewTopup: async (amountCents: number): Promise<BonusPreview> => {
    const payload = await apiRequest<unknown>(
      `${API_PATHS.credits.topupPreview}?amountCents=${encodeURIComponent(String(amountCents))}`
    );
    return unwrap<BonusPreview>(payload);
  },
  topUp: async (
    input: { amountCents: number; paymentMethodId?: string },
    opts?: { stepUpToken?: string; idempotencyKey?: string }
  ): Promise<TopupResult> => {
    const idempotencyKey =
      opts?.idempotencyKey?.trim() ||
      buildIdempotencyKey('credits-topup', [input.amountCents, input.paymentMethodId || 'default']);
    const headers: Record<string, string> = {};
    if (opts?.stepUpToken) headers['X-Step-Up-Token'] = opts.stepUpToken;
    const payload = await apiRequest<unknown>(API_PATHS.credits.topup, {
      method: 'POST',
      body: JSON.stringify(input),
      idempotencyKey,
      requireIdempotencyKey: true,
      headers
    });
    const result = unwrap<TopupResult>(payload);
    if (!Number.isFinite(Number(result?.newBalance)) || !Number.isFinite(Number(result?.topUpCents))) {
      throw new Error('Top-up was not confirmed by the server. Try again.');
    }
    return result;
  },
  requestCashOut: async (
    amountCents: number,
    opts?: { stepUpToken?: string; idempotencyKey?: string }
  ): Promise<CashOutResult> => {
    const idempotencyKey =
      opts?.idempotencyKey?.trim() || buildIdempotencyKey('credits-cashout', [amountCents]);
    const headers: Record<string, string> = {};
    if (opts?.stepUpToken) headers['X-Step-Up-Token'] = opts.stepUpToken;
    const payload = await apiRequest<unknown>(API_PATHS.credits.cashout, {
      method: 'POST',
      body: JSON.stringify({ amountCents }),
      idempotencyKey,
      requireIdempotencyKey: true,
      headers
    });
    const result = unwrap<CashOutResult>(payload);
    if (!String(result?.approvalId || '').trim()) {
      throw new Error('Cash-out request was not filed. Try again.');
    }
    return result;
  }
};
